// NodeMissionsList.jsx

import React, { useMemo } from 'react';

// Context
import { useTheme } from 'context/ThemeContext';

// Utilities
import {
  NODE_MISSION_STATUS_LABELS,
} from 'node/missions/mission/utils/missionRunUtils';

// CSS
import './NodeMissionsList.css';

// Icons
import Icon from 'svg/Icon';

// Components
import Button from 'components/button/Button';

function normalizeString(value) {
  return typeof value === 'string' ? value.trim() : '';
}

/**
 * countPlaybookTemplateSteps
 *
 * @param {object|null} template
 * @returns {number}
 */
function countPlaybookTemplateSteps(template) {
  return (Array.isArray(template?.tasks) ? template.tasks : [])
    .filter((task) => String(task?.key || '').trim())
    .length;
}

/**
 * resolveMissionListMeta
 *
 * @param {object} entry
 * @returns {string}
 */
function resolveMissionListMeta(entry) {
  const hasRun = Boolean(entry?.missionRun);
  if (!hasRun) {
    const description = normalizeString(entry?.template?.description);
    const stepCount = countPlaybookTemplateSteps(entry?.template);
    const stepLabel = stepCount === 1 ? '1 step' : `${stepCount} steps`;

    if (description) {
      return stepCount > 0 ? `${stepLabel} · ${description}` : description;
    }

    return stepCount > 0 ? `${stepLabel} · Tap to start` : 'Tap to start';
  }

  const statusLabel = NODE_MISSION_STATUS_LABELS[entry?.missionStatus] || 'In Progress';
  const complete = Number(entry?.missionProgress?.complete || 0);
  const total = Number(entry?.missionProgress?.total || 0);

  if (total > 0) {
    return `${statusLabel} · ${complete}/${total} steps`;
  }

  return statusLabel;
}

/**
 * NodeMissionsList
 *
 * Playbooks chooser: short intro + named playbook cards.
 *
 * @param {object[]} [missionEntries]
 * @param {function} [onSelectMission]
 * @param {string} [singularTypeLabel]
 * @returns {ReactNode}
 */
const NodeMissionsList = React.memo(({
  missionEntries = [],
  onSelectMission,
  singularTypeLabel = 'record',
}) => {
  // ----- CONTEXT ----- //

  const { theme } = useTheme();

  // ----- MEMOS ----- //

  const typeLabel = normalizeString(singularTypeLabel).toLowerCase() || 'record';

  const items = useMemo(() => (
    (Array.isArray(missionEntries) ? missionEntries : []).map((entry) => {
      const hasRun = Boolean(entry?.missionRun);
      return {
        key: entry?.template?.key,
        hasRun,
        iconKey: normalizeString(entry?.template?.iconKey) || 'account_tree',
        title: normalizeString(entry?.template?.title) || 'Untitled Playbook',
        meta: resolveMissionListMeta(entry),
      };
    }).filter((item) => item.key)
  ), [missionEntries]);

  const introTitle = items.some((item) => item.hasRun)
    ? 'Playbooks'
    : 'Kick things off with a playbook...';

  const introCopy = items.some((item) => item.hasRun)
    ? 'Open a playbook in progress, or start another for this record.'
    : `Each playbook is a guided and automated checklist of tasks for this ${typeLabel}.`;

  // ----- RENDER ----- //

  return (
    <div className="node-missions-list">

      {/* INTRO 
      <div className="node-missions-list-intro">
        <div className="node-missions-list-intro-title">
          {introTitle}
        </div>
        <div
          className="node-missions-list-intro-copy"
          style={{ color: theme.foregroundMuted }}
        >
          {introCopy}
        </div>
      </div>
      */}

      {/* ITEMS */}
      <div className="node-missions-list-items">
        {items.map((item) => {
          const handleSelect = () => onSelectMission?.(item.key);

          return (
            <div
              key={item.key}
              className="node-missions-list-item"
              role="button"
              tabIndex={0}
              onClick={handleSelect}
              onKeyDown={(event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                  event.preventDefault();
                  handleSelect();
                }
              }}
            >
              <div
                className="node-missions-list-item-icon"
                style={{
                  backgroundColor: theme.backgroundMuted,
                }}
              >
                <Icon
                  iconKey={item.iconKey || 'account_tree'}
                  color={theme.foreground}
                  width={20}
                  height={20}
                />
              </div>

              <div className="node-missions-list-item-copy">
                <div className="node-missions-list-item-title">
                  {item.title}
                </div>
                <div
                  className="node-missions-list-item-meta"
                  style={{ color: theme.foregroundMuted }}
                >
                  {item.meta}
                </div>
              </div>

              {item.hasRun ? (
                <Icon
                  iconKey="chevron_right"
                  color={theme.foregroundMuted}
                  width={18}
                  height={18}
                />
              ) : (
                <div className="node-missions-list-item-action">
                  <Button
                    iconKey="play_arrow"
                    iconSize={18}
                    label="Start"
                    onClick={handleSelect}
                  />
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
});

NodeMissionsList.displayName = 'NodeMissionsList';

export default NodeMissionsList;
